import { useState, useEffect, useRef } from "react";

const slides = [
  {
    img: "/images/hero/welding.webp",
    headline: "Stainless & TIG Welding",
    sub: "Precision stainless steel fabrication, pipework and food-grade welding — built in our Nairobi workshop.",
  },
  {
    img: "/images/hero/engineering.webp",
    headline: "Engineering & HVAC",
    sub: "Design, HVAC, process plant and automation — engineered from drawing to commissioning.",
  },
  {
    img: "/images/hero/kitchen.webp",
    headline: "Commercial Kitchens",
    sub: "Custom SS304 kitchens, extraction canopies and cold rooms for hotels, hospitals and restaurants.",
  },
];

const stats = [
  { value: 350, suffix: "+", label: "Projects Completed" },
  { value: 15, suffix: "+", label: "Years Experience" },
  { value: 98, suffix: "%", label: "Client Satisfaction" },
  { value: 24, suffix: "/7", label: "Support" },
];

function useCounter(target: number, running: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!running) return;
    let start = 0;
    const duration = 2000;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, running]);
  return count;
}

function StatItem({ stat, running }: { stat: typeof stats[0]; running: boolean }) {
  const count = useCounter(stat.value, running);
  return (
    <div className="flex flex-col items-center text-center">
      <p className="text-3xl font-extrabold text-white leading-none">
        {count}<span className="text-teal-400">{stat.suffix}</span>
      </p>
      <p className="text-white/60 text-xs mt-1 uppercase tracking-wider">{stat.label}</p>
    </div>
  );
}

const particles = [
  { top: "15%", left: "8%", size: 6, delay: "0s", dur: "5s" },
  { top: "25%", left: "85%", size: 4, delay: "1s", dur: "7s" },
  { top: "65%", left: "5%", size: 8, delay: "2s", dur: "6s" },
  { top: "80%", left: "78%", size: 5, delay: "0.5s", dur: "8s" },
  { top: "40%", left: "92%", size: 3, delay: "3s", dur: "5s" },
  { top: "55%", left: "15%", size: 7, delay: "1.5s", dur: "9s" },
  { top: "10%", left: "55%", size: 4, delay: "2.5s", dur: "6s" },
  { top: "88%", left: "40%", size: 5, delay: "0.8s", dur: "7s" },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const [statsRunning, setStatsRunning] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setVisible(true);
      }, 600);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsRunning(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const { clientX, clientY, currentTarget } = e;
    const { width, height } = currentTarget.getBoundingClientRect();
    setMousePos({ x: (clientX / width - 0.5) * 12, y: (clientY / height - 0.5) * 8 });
  };

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const slide = slides[current];

  return (
    <section
      id="home"
      className="relative w-full h-screen min-h-[700px] overflow-hidden cursor-default"
      onMouseMove={handleMouseMove}
    >
      {/* Background slides with parallax */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <img
            src={s.img}
            alt={s.headline}
            loading={i === 0 ? "eager" : "lazy"}
            className="w-full h-full object-cover object-right transition-transform duration-300"
            style={{ transform: `scale(1.08) translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)` }}
          />
        </div>
      ))}

      {/* Multi-layer overlay — deep teal/cyan brand atmosphere */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/45 to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-slate-950/30"></div>
      {/* Radial glow from center-left */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(6,182,212,0.12) 0%, transparent 65%)" }}></div>
      {/* Top edge glow */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent"></div>

      {/* Floating particles — vibrant cyan */}
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-cyan-400/50 animate-particle"
          style={{
            top: p.top, left: p.left,
            width: p.size, height: p.size,
            animationDelay: p.delay, animationDuration: p.dur,
          }}
        />
      ))}

      {/* Decorative spinning rings — cyan brand colour */}
      <div className="absolute right-[15%] top-[20%] w-64 h-64 border border-cyan-400/25 rounded-full animate-spin-slow hidden lg:block"></div>
      <div className="absolute right-[18%] top-[23%] w-48 h-48 border border-teal-300/20 rounded-full animate-spin-slow hidden lg:block" style={{ animationDirection: "reverse", animationDuration: "15s" }}></div>
      {/* Extra outer ring */}
      <div className="absolute right-[12%] top-[17%] w-80 h-80 border border-cyan-500/10 rounded-full animate-spin-slow hidden lg:block" style={{ animationDuration: "30s" }}></div>

      {/* Main content */}
      <div className="relative z-10 w-full h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-20">

          {/* Left — text */}
          <div>
            {/* Animated badge — cyan brand */}
            <div
              className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6 animate-slide-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400"></span>
              </span>
              20% OFF — Limited Time Offer
            </div>

            {/* Headline */}
            <div
              className="opacity-0-init animate-fadeInLeft"
              style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}
            >
              <h2 className="text-white font-extrabold leading-[1.05] tracking-tight mb-2" style={{ fontSize: "clamp(2.8rem, 6vw, 5rem)" }}>
                ISMAN
              </h2>
              <h2
                className="font-extrabold leading-[1.05] tracking-tight mb-2"
                style={{
                  fontSize: "clamp(2.8rem, 6vw, 5rem)",
                  background: "linear-gradient(90deg, #22d3ee, #06b6d4, #2dd4bf, #22d3ee)",
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shimmer 4s linear infinite",
                }}
              >
                {slide.headline}
              </h2>
            </div>

            <p
              className="text-white/75 text-lg mt-4 max-w-lg leading-relaxed opacity-0-init animate-fadeInUp"
              style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}
            >
              {slide.sub}
            </p>
            <p
              className="text-white/60 text-sm mt-1 max-w-lg opacity-0-init animate-fadeInUp"
              style={{ animationDelay: "0.65s", animationFillMode: "forwards" }}
            >
              Expert HVAC, kitchen design, plumbing, welding &amp; more across Kenya.
            </p>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-3 mt-8 opacity-0-init animate-fadeInUp"
              style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}
            >
              <button
                onClick={() => handleNav("#contact")}
                className="group relative overflow-hidden bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-8 py-3.5 rounded-lg transition-all duration-300 uppercase tracking-wider text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500 skew-x-12"></span>
                <i className="ri-calendar-check-line"></i>
                Book a Consultation
              </button>
              <button
                onClick={() => handleNav("#services")}
                className="border-2 border-cyan-400/50 hover:border-cyan-400 hover:text-cyan-300 text-white/80 font-semibold px-8 py-3.5 rounded-lg transition-all duration-300 uppercase tracking-wider text-sm cursor-pointer whitespace-nowrap"
              >
                Explore Services
              </button>
            </div>

            {/* Quick contacts */}
            <div
              className="flex flex-col sm:flex-row gap-4 mt-8 opacity-0-init animate-fadeInUp"
              style={{ animationDelay: "1s", animationFillMode: "forwards" }}
            >
              <a href="tel:0724114555" className="flex items-center gap-2.5 text-white/70 hover:text-cyan-300 text-sm transition-colors cursor-pointer group">
                <span className="w-8 h-8 flex items-center justify-center bg-cyan-600/25 border border-cyan-400/40 rounded-full group-hover:bg-cyan-600/50 transition-colors">
                  <i className="ri-phone-line text-cyan-400 text-sm"></i>
                </span>
                072 411 4555
              </a>
              <a href="mailto:info@isman.co.ke" className="flex items-center gap-2.5 text-white/70 hover:text-cyan-300 text-sm transition-colors cursor-pointer group">
                <span className="w-8 h-8 flex items-center justify-center bg-cyan-600/25 border border-cyan-400/40 rounded-full group-hover:bg-cyan-600/50 transition-colors">
                  <i className="ri-mail-line text-cyan-400 text-sm"></i>
                </span>
                info@isman.co.ke
              </a>
            </div>
          </div>

          {/* Right — 3D floating stats card */}
          <div
            className="hidden lg:flex justify-center items-center opacity-0-init animate-fadeInRight"
            style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}
          >
            <div
              className="animate-float relative"
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Glow behind card — cyan brand */}
              <div className="absolute inset-0 bg-cyan-500/25 rounded-2xl blur-2xl scale-110"></div>

              {/* 3D Stats Panel */}
              <div
                className="relative bg-cyan-950/60 backdrop-blur-md border border-cyan-400/25 rounded-2xl p-8 w-[340px]"
                style={{ transform: "perspective(1000px) rotateY(-8deg) rotateX(4deg)", boxShadow: "0 40px 80px rgba(0,0,0,0.5), 0 0 40px rgba(6,182,212,0.15), inset 0 1px 0 rgba(255,255,255,0.15)" }}
              >
                {/* Top label */}
                <div className="flex items-center gap-2 mb-6">
                  <img
                    src="/logo.png"
                    alt="ISMAN"
                    className="h-8 w-auto object-contain"
                  />
                  <div>
                    <p className="text-white text-xs font-bold uppercase tracking-widest">ISMAN</p>
                    <p className="text-cyan-400 text-[10px] uppercase tracking-widest">Engineering</p>
                  </div>
                </div>

                {/* Stats grid */}
                <div ref={statsRef} className="grid grid-cols-2 gap-5 mb-6">
                  {stats.map((s) => (
                    <StatItem key={s.label} stat={s} running={statsRunning} />
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-cyan-400/15 pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400"></span>
                    </span>
                    <p className="text-white/70 text-xs">Active Projects in Kenya</p>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: "Commercial Kitchens", pct: 85 },
                      { label: "HVAC Systems", pct: 72 },
                      { label: "Hospital Equipment", pct: 60 },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-xs text-white/60 mb-1">
                          <span>{item.label}</span><span>{item.pct}%</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-cyan-400 to-teal-400 rounded-full"
                            style={{ width: `${item.pct}%`, transition: "width 2s ease" }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Inner reflection */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent rounded-t-2xl"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-cyan-400/30 to-transparent rounded-l-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 left-6 md:left-14 flex items-center gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => { setCurrent(i); setVisible(true); }}
            className={`h-1 rounded-full transition-all duration-400 cursor-pointer ${i === current ? "w-10 bg-gradient-to-r from-cyan-400 to-teal-400" : "w-3 bg-white/30 hover:bg-white/60"}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-8 z-10 flex flex-col items-center gap-1 text-white/40">
        <span className="text-[10px] tracking-[0.2em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent relative overflow-hidden">
          <div className="absolute w-full h-4 bg-cyan-400/80 animate-float-slow"></div>
        </div>
      </div>

      {/* Slide counter */}
      <div
        className="absolute top-24 right-8 z-10 text-white/30 text-xs font-mono hidden md:block transition-all duration-500"
        style={{ opacity: visible ? 1 : 0 }}
      >
        0{current + 1} / 0{slides.length}
      </div>
    </section>
  );
}
